import { FC } from "react";
import { Element } from "@craftjs/core";
import { Title } from "@/src/components/Title";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/src/components/ui/carousel";
import { getProductsList } from "@/src/api/services/products";
import { useQuery } from "@tanstack/react-query";
import { ProductCardDemo as ProductCard } from "@/src/components/ProductCardDemo/ProductCardDemo";
import { useParams } from "next/navigation";
import { Box } from "@/src/components/Box";
import { Section } from "@/src/components/Section";
import { Container } from "@/src/components/Container";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { isMobile } from "react-device-detect";
import { ProductStatus } from "@/src/shared/const";

export function ProductCarousel({
  options = [],
  asListOnMobile = false,
}: {
  options?: { value: string }[];
  asListOnMobile?: boolean;
}) {
  const { productSlug } = useParams<{ productSlug: string }>();
  const { data: products } = useQuery({
    queryKey: ["PRODUCT_KEY", productSlug],
    queryFn: () => getProductsList({ slug: productSlug }),
    enabled: !!productSlug,
    select: (data) => data.content[0].relatedProducts,
  });

  return (
    <Section className={"py-10"}>
      <Container className={cn("max-w-[1440px] px-0")}>
        <Carousel
          className={"w-full"}
          opts={{
            align: "start",
            loop: true,
            active: !(isMobile && asListOnMobile),
          }}
          orientation={isMobile && asListOnMobile ? "vertical" : "horizontal"}
        >
          <div className="mb-4 flex items-center justify-between">
            <Element
              is={Title}
              id={"carousel-title"}
              text={"Можливо, вас зацікавить:"}
              size={"h3"}
              background={"#234678"}
            />
            <div
              className={cn("flex gap-2", { "hidden md:flex": asListOnMobile })}
            >
              <CarouselPrevious />
              <CarouselNext />
          </div>
          </div>
          <CarouselContent className="-ml-0">
            {products
              ?.filter(
                (item) =>
                  options.some((option) => option.value === item.code) &&
                    (item.status === ProductStatus.ACTIVE || item.status === ProductStatus.INACTIVE)
              )
              .sort(
                (a, b) =>
                  options.findIndex((option) => option.value === a.code) -
                  options.findIndex((option) => option.value === b.code),
              )
              .map((item) => (
                <CarouselItem
                  key={item.id}
                  className={"pl-0 md:basis-1/2 lg:basis-1/3"}
                >
                  <Element
                    is={Box}
                    id={`product-${item.id}-${item.status}`}
                    style={{ padding: 8 }}
                    className={"h-full"}
                  >
                    <ProductCard
                      productId={item.id}
                      slug={item.slug}
                      title={item.title}
                      description={item.description}
                      imageUrl={item.imageUrl}
                      termsUrl={item.termsUrl}
                      status={item.status || "ACTIVE"}
                      mode={"manual"}
                    />
                  </Element>
                </CarouselItem>
              ))}
          </CarouselContent>
        </Carousel>
      </Container>
    </Section>
  );
}
